/**
 * Font catalogue + on-demand web-font loading for the print editor.
 *
 * The editor offers the whole Google Fonts library (the same library Figma's
 * font picker is built on), which is ~1900 families and far too much to put in
 * a stylesheet up front. So nothing is loaded until it is needed:
 *
 *  - PREVIEWS in the picker load a subset containing just the family's own name,
 *    under an aliased family, so a list of two thousand rows costs a few KB.
 *  - APPLYING a font loads the real family for real use.
 *
 * The alias matters. Google's `&text=` responses carry no unicode-range, so a
 * preview face registered under the family's true name would shadow the full
 * font and leave every character outside the family name rendering from a
 * fallback. Rewriting the preview's family name keeps the two completely apart.
 */
import { CATEGORIES, SCRIPTS, FAMILY_ROWS } from './catalogData';
import { clearGlyphMetrics } from '../utils/textPathLayout';

const GF_CSS = 'https://fonts.googleapis.com/css2';

/** Suffix that separates a preview-only face from the real family. */
const PREVIEW_SUFFIX = '  fpv'; // thin space keeps it out of real family names

export const WEIGHT_STEPS = [100, 200, 300, 400, 500, 600, 700, 800, 900];

export const WEIGHT_NAMES = {
    100: 'Thin',
    200: 'ExtraLight',
    300: 'Light',
    400: 'Regular',
    500: 'Medium',
    600: 'SemiBold',
    700: 'Bold',
    800: 'ExtraBold',
    900: 'Black',
};

/** Google/Figma naming: 400 italic is just "Italic", the rest are "<Name> Italic". */
export function variantLabel(weight, italic) {
    const base = WEIGHT_NAMES[weight] || String(weight);
    if (!italic) return base;
    return weight === 400 ? 'Italic' : `${base} Italic`;
}

/**
 * Families the browser already has. They need no network fetch, and an editor
 * for print work should not lose them just because they are not on Google.
 */
const SYSTEM_FAMILIES = [
    'Arial', 'Helvetica', 'Times New Roman', 'Georgia', 'Courier New',
    'Verdana', 'Tahoma', 'Trebuchet MS', 'Palatino Linotype',
];

const decodeWeights = (mask) => {
    const out = [];
    WEIGHT_STEPS.forEach((weight, i) => {
        if (mask & (1 << i)) out.push({ weight, italic: false });
    });
    WEIGHT_STEPS.forEach((weight, i) => {
        if (mask & (1 << (i + 9))) out.push({ weight, italic: true });
    });
    return out;
};

const googleFamilies = FAMILY_ROWS.map(([name, cat, weightMask, scriptMask]) => ({
    name,
    category: CATEGORIES[cat] || CATEGORIES[0],
    google: true,
    weightMask,
    scriptMask,
    variants: decodeWeights(weightMask),
}));

const systemFamilies = SYSTEM_FAMILIES.map((name) => ({
    name,
    category: 'System',
    google: false,
    weightMask: 0,
    scriptMask: 0,
    // A local face is whatever the OS has; offer the four the browser can
    // synthesise for any family rather than pretending to know its real cuts.
    variants: [
        { weight: 400, italic: false },
        { weight: 400, italic: true },
        { weight: 700, italic: false },
        { weight: 700, italic: true },
    ],
}));

/** Every family the picker offers, alphabetical, system fonts first. */
export const FONT_CATALOG = [...systemFamilies, ...googleFamilies];

const byName = new Map(FONT_CATALOG.map((f) => [f.name.toLowerCase(), f]));

export function getFamily(name) {
    return byName.get(String(name || '').trim().toLowerCase()) || null;
}

export const FONT_CATEGORIES = ['System', ...CATEGORIES];
export const FONT_SCRIPTS = SCRIPTS;

/**
 * The variants a family actually ships. Unknown families (a legacy template
 * naming a font we no longer list, or a local corporate face) still get the
 * four synthesisable cuts so their dropdown is never empty.
 */
export function familyVariants(name) {
    const fam = getFamily(name);
    if (fam) return fam.variants;
    return [
        { weight: 400, italic: false },
        { weight: 400, italic: true },
        { weight: 700, italic: false },
        { weight: 700, italic: true },
    ];
}

/**
 * Closest available variant to a requested weight/style.
 *
 * Applying a family that has no 400 (Abril Fatface is 400-only, Bitter starts at
 * 100, some display faces are 700-only) must not leave the style dropdown
 * showing a cut the family does not have — the browser would synthesise it and
 * the export would not match. Italic is preferred but not required.
 */
export function resolveVariant(name, weight = 400, italic = false) {
    const variants = familyVariants(name);
    if (!variants.length) return { weight: 400, italic: false };
    const exact = variants.find((v) => v.weight === weight && v.italic === italic);
    if (exact) return exact;
    const sameSlant = variants.filter((v) => v.italic === italic);
    const pool = sameSlant.length ? sameSlant : variants;
    return pool.reduce((best, v) => (
        Math.abs(v.weight - weight) < Math.abs(best.weight - weight) ? v : best
    ), pool[0]);
}

/* ------------------------------------------------------------------ *
 *  Loading
 * ------------------------------------------------------------------ */

const familyParam = (name) => name.trim().replace(/\s+/g, '+');

/**
 * css2 URL for a set of variants. The API rejects anything out of order, so the
 * tuples are sorted by italic then weight exactly as it demands.
 */
function cssUrl(name, variants) {
    // Deduplicate first. Asking for the same cut twice — which happens whenever a
    // caller wants Regular and Bold from a family that only ships one weight, so
    // `resolveVariant` returns the same cut for both — produces `wght@400;400`,
    // and css2 answers that with a 400, leaving the family with no face at all.
    const seen = new Set();
    const cuts = variants.filter((v) => {
        const key = `${v.italic ? 1 : 0}:${v.weight}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    }).sort((a, b) => (a.italic - b.italic) || (a.weight - b.weight));
    const hasItalic = cuts.some((v) => v.italic);
    let spec = familyParam(name);
    if (hasItalic) {
        spec += `:ital,wght@${cuts.map((v) => `${v.italic ? 1 : 0},${v.weight}`).join(';')}`;
    } else if (cuts.length > 1 || cuts[0]?.weight !== 400) {
        spec += `:wght@${cuts.map((v) => v.weight).join(';')}`;
    }
    return `${GF_CSS}?family=${spec}&display=swap`;
}

const injected = new Map(); // href → Promise that settles once the stylesheet has arrived
const previewFaces = new Map(); // family → Promise<boolean>
const pendingByFamily = new Map(); // family → Promise for every stylesheet requested for it

/**
 * Add a font stylesheet once. Resolves when the browser has fetched and parsed
 * it — only then do the family's @font-face rules exist, and only then can
 * `document.fonts.load` find a face to load.
 */
function injectLink(href) {
    if (injected.has(href)) return injected.get(href);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.crossOrigin = 'anonymous';
    const ready = new Promise((resolve) => {
        link.onload = () => resolve();
        link.onerror = () => resolve(); // an unreachable stylesheet just means a fallback face
    });
    injected.set(href, ready);
    document.head.appendChild(link);
    return ready;
}

/**
 * Make a family available for real use. Loads every cut it has unless specific
 * variants are named — a template that only uses Bold has no reason to pull the
 * other eight weights.
 */
export function ensureFontFamily(name, variants = null) {
    const fam = getFamily(name);
    if (!fam || !fam.google) return; // system face, or one we don't host
    const cuts = variants?.length ? variants : fam.variants;
    if (!cuts.length) return;
    const ready = injectLink(cssUrl(fam.name, cuts));
    pendingByFamily.set(fam.name, Promise.all([pendingByFamily.get(fam.name), ready]));
}

/** Wait until a specific cut is actually usable for measuring/drawing. */
/**
 * How long anything may wait for a web font before carrying on without it.
 *
 * `document.fonts.load` waits for the fetch, and a fetch on a slow connection —
 * or one Google Fonts never answers — takes as long as it takes. Everything
 * that measures text awaits this first, so an unbounded wait is not a slow
 * font: it is a dead click. Measuring against the fallback face is a little
 * wrong; waiting forever is unusable.
 */
const FONT_WAIT_MS = 3500;

export async function whenFontReady(name, weight = 400, italic = false, size = 32, text = '') {
    if (typeof document === 'undefined' || !document.fonts?.load) return;
    const style = italic ? 'italic' : 'normal';
    try {
        await Promise.race([
            // `text` matters: a Google family is delivered as several files, each
            // covering a range of characters (latin, korean, bengali…), and the
            // browser only fetches the ones the probe text needs. Without it the
            // probe is a single space, which loads one file — often not the one
            // holding the letters that are about to be drawn.
            // The stylesheet first: until it has arrived there is no face to load, so
            // an early `fonts.load` finds nothing, resolves, and the caller carries
            // on — measuring and painting a fallback that is never corrected.
            (pendingByFamily.get(name) || Promise.resolve())
                .then(() => document.fonts.load(`${style} ${weight} ${size}px "${name}"`, text || undefined)),
            new Promise((resolve) => { setTimeout(resolve, FONT_WAIT_MS); }),
        ]);
    } catch { /* an unavailable face just falls back */ }
}

/** Load a family and wait for one cut of it — what "apply this font" needs. */
export async function applyFontFamily(name, weight = 400, italic = false) {
    ensureFontFamily(name);
    await whenFontReady(name, weight, italic);
    // The face just changed under any cached glyph advance for this family.
    clearGlyphMetrics();
}

/** The aliased family a preview row should render in. */
export const previewFamily = (name) => `${name}${PREVIEW_SUFFIX}`;

/**
 * Load a name-sized subset of a family under its preview alias.
 *
 * Resolves to false when the family has none of the glyphs its own name needs
 * (a Devanagari-only face named in Latin, say) so the caller can leave that row
 * in the UI font instead of showing an empty line.
 */
export function ensureFontPreview(name) {
    const fam = getFamily(name);
    if (!fam) return Promise.resolve(false);
    // Nothing to fetch for a face the OS already has.
    if (!fam.google) return Promise.resolve(true);
    if (previewFaces.has(name)) return previewFaces.get(name);

    // Preview in the family's own default-ish weight so the row reads as designed.
    const cut = resolveVariant(name, 400, false);
    const url = `${cssUrl(name, [cut])}&text=${encodeURIComponent(name)}`;

    const task = (async () => {
        const res = await fetch(url);
        if (!res.ok) return false;
        const css = await res.text();
        // Re-label the face so this stripped subset can never stand in for the
        // real family once the user actually applies it.
        const aliased = css.replace(
            /font-family:\s*(['"])(.*?)\1/g,
            (_m, q, fam2) => `font-family: ${q}${fam2}${PREVIEW_SUFFIX}${q}`,
        );
        if (!aliased.includes(PREVIEW_SUFFIX)) return false;
        const style = document.createElement('style');
        style.dataset.fontPreview = name;
        style.textContent = aliased;
        document.head.appendChild(style);
        await whenFontReady(previewFamily(name), cut.weight, cut.italic, 16);
        return true;
    })().catch(() => false);

    previewFaces.set(name, task);
    return task;
}

/**
 * Load every font a document needs, in the cuts it actually uses.
 *
 * Both the canvas and the export measure text, so this has to finish before
 * either runs — otherwise the first paint is laid out with a fallback face and
 * every line breaks in the wrong place.
 */
export async function ensureDocumentFonts(elements = []) {
    const wanted = new Map(); // family → Set of "weight:italic"
    const want = (family, weight, italic) => {
        const name = family || 'Hind Siliguri';
        if (!wanted.has(name)) wanted.set(name, new Set());
        wanted.get(name).add(`${weight}:${italic ? 1 : 0}`);
    };

    elements.forEach((el) => {
        const s = el?.styles || {};
        if (!s.fontFamily && el?.type !== 'text' && el?.type !== 'placeholder') return;
        want(s.fontFamily, Number(s.fontWeight) || 400, s.fontStyle === 'italic');
    });
    // The Bengali fallback in resolveFontStack must be there too, or Bengali
    // glyphs missing from the chosen family render from a different face than
    // the design was laid out with.
    want('Noto Sans Bengali', 400, false);

    wanted.forEach((cuts, name) => {
        ensureFontFamily(name, [...cuts].map((c) => {
            const [w, i] = c.split(':');
            return resolveVariant(name, Number(w), i === '1');
        }));
    });

    await Promise.allSettled([...wanted].flatMap(([name, cuts]) => [...cuts].map((c) => {
        const [w, i] = c.split(':');
        return whenFontReady(name, Number(w), i === '1');
    })));
    if (document.fonts?.ready) {
        try { await document.fonts.ready; } catch { /* ignore */ }
    }
    // Glyph advances for text-on-path are cached; anything measured before the real
    // faces arrived was measured against a fallback, so drop them.
    clearGlyphMetrics();
}
